import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { omitBy, isNil, isEmpty, omit } from 'lodash';
import errorHandler from 'Utils/error-handler';

import { ArrowRightOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Space } from 'antd';
import { AlertBox, BasicModal, Button, Password, PhoneInput, SelectInput, TextInput } from '@life-line-lab/components-react';

import AccountInfoFormSchema from './account-info-form.validation';
import parsePhoneNumberFromString from 'libphonenumber-js'
import { checkEmailExistsInCMS } from 'Services/profile.services';

interface AccountInfoFormProps {
  accountType: "BUSINESS_ACCOUNT" | "INDIVIDUAL_ACCOUNT";
  status?: string;
  data?: any;
  onSubmit: (input: any) => void;
}

const AccountInfoForm: React.FC<AccountInfoFormProps> = ({ status, data, onSubmit, accountType }) => {
  const history = useHistory();

  const initialInput = {
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    idType: 'NRIC',
    idNumber: '',
    gender: '',
    dob: '',
    mobilePhone: { dialCode: '', number: '' },
  }

  const initialError = {}
  const { t } = useTranslation();

  const [input, setInput] = useState<any>(initialInput);
  const [error, setError] = useState<any>(initialError);
  const [showEmailExistPopup, setShowEmailExistPopup] = useState(false);
  const [validatedValue, setValidatedValue] = useState<any>(undefined);

  const onCloseModal = () => {
    setShowEmailExistPopup(false);
  }

  useEffect(() => {
    if (!isEmpty(data)) {
      let parsed: any;
  
      if (typeof data.mobilePhone === 'string') {
        parsed = parsePhoneNumberFromString(data.mobilePhone)
      } else if (data.mobilePhone?.dialCode && data.mobilePhone?.number) {
        parsed = {
          countryCallingCode: data.mobilePhone?.dialCode,
          nationalNumber: data.mobilePhone?.number,
        }
      }

      const mobilePhone = { dialCode: parsed?.countryCallingCode , number: parsed?.nationalNumber }
  
      setInput({ ...input, ...data, mobilePhone });
    } 
  }, [data])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(initialError);
    setInput({ ...input, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value: string | string[], name: string) => {
    setError(initialError);
    setInput({ ...input, [name]: value });
  }

  const handlePhoneChange = (value: { dialCode: string, number: string }, name: string) => {
    setError(initialError);
    setInput({ ...input, [name]: value });
  }

  const handleSubmit = async () => {
    try {
      const value = AccountInfoFormSchema.validateSync(omitBy({
        ...input,
        // mobilePhone: input.mobilePhone?.dialCode && input.mobilePhone?.number ?
        // `+${input.mobilePhone!.dialCode}${input.mobilePhone!.number}` : null,
      }, isNil), {
        strict: true,
        abortEarly: true,
        stripUnknown: false
      });

      if (!isEmpty(value)) {
        if (accountType === 'BUSINESS_ACCOUNT') {
          const emailAlreadyExist = await checkEmailExistsInCMS(value.email);
          if (emailAlreadyExist) {
            setValidatedValue(value);
            setShowEmailExistPopup(true);
            // popup, and then let the user continue
            return;
          }
        }

        onSubmit(value);
      }
    }
    catch(err) {
      let { path, refPath, value } = errorHandler.validation(err) || {};

      if (!isNil(refPath)) {
        setError({
          ...error,
          [path]: t(`${value}`, {
            field: t(`label.${path}`),
            refPath: refPath ? t(`label.${refPath}`) : null
          })
        });
      }
      else {
        setError({
          ...error,
          [path]: t(`${value}`, { field: t(`label.${path}`) })
        });
      }

      try {
        if (document.querySelector(`input[name=${path}]`)) {
          (document.querySelector(`input[name=${path}]`) as HTMLInputElement).focus();
        }
        else if (document.querySelector(`div[name=${path}]`)) {
          (document.querySelector(`div[name=${path}] input`) as HTMLInputElement).focus();
        }
      } catch (errFocus) {
      }
    }
  }

  return (
    <div className="account-info-form">
      <form>
      <Space className="account-info-form--inner" direction="vertical" size="small">
        <div>
          <TextInput
            isError={!!error.fullName}
            isRequired={true}
            label={t('label.fullName')}
            name="fullName"
            value={input.fullName}
            onChange={handleChange}
          />
          <AlertBox text={error.fullName} />
        </div>

        <div>
          <SelectInput
            isError={!!error.idType}
            label={t('label.idType')}
            name="idType"
            value={input.idType}
            placeholder={t('option.selectOne')}
            onChange={
              (value: string | string[]) =>
                handleSelectChange(value, 'idType')
            }
            isRequired={true}
            options={[
              { value: 'NRIC', label: t('option.nric') },
              { value: 'PASSPORT', label: t('option.passport') },
              { value: 'FOREIGN_ID', label: t('option.foreignId') },
            ]}
          />
          <AlertBox text={error.idType} />
        </div>

        <div>
          <TextInput
            isError={!!error.idNumber}
            isRequired={true}
            label={input.idType === 'PASSPORT' ? t('label.passport') : t('label.idNumber')}
            name="idNumber"
            value={input.idNumber}
            onChange={handleChange}
          />
          <AlertBox text={error.idNumber} />
        </div>

        <div>
          <TextInput
            isError={!!error.email}
            isRequired={true}
            label={t('label.email')}
            name="email"
            value={input.email}
            placeholder="yourname@email.com"
            onChange={handleChange}
          />
          <AlertBox text={error.email} />
        </div>

        <div>
          <PhoneInput
            isRequired={true}
            isError={!!error.mobilePhone}
            label={t('label.mobilePhone')}
            name="mobilePhone"
            value={input.mobilePhone}
            onChange={(value) => handlePhoneChange(value, 'mobilePhone')}
          />
          <AlertBox text={error.mobilePhone} />
          <AlertBox text={error['mobilePhone.number']} />
          <AlertBox text={error.mobilePhoneNumberOnly} />
        </div>

        <div>
          <Password
            isError={!!error.password}
            isRequired={true}
            label={t('label.password')}
            name="password"
            value={input.password}
            onChange={handleChange}
          />
          <AlertBox text={error.password} />
        </div>

        <div>
          <Password
            isError={!!error.confirmPassword}
            isRequired={true}
            label={t('label.confirmPassword')}
            name="confirmPassword"
            value={input.confirmPassword}
            onChange={handleChange}
          />
          <AlertBox text={error.confirmPassword} />
        </div>

        <div>
          {/* <span>{t('txt.passwordFormat')}</span> */}
          <Space direction="vertical" size="small">
            <span className='note'>
              <ul>
                <li>{t('txt.minimumSixCharacters')}</li>
                <li>{t('txt.maximumTwelveCharacters')}</li>
                <li dangerouslySetInnerHTML={{ __html: t('txt.alphanumeric') }} />
              </ul>
            </span>
            {/* <span> - </span>
            <span> - {t('txt.maximumTwelveCharacters')}</span>
            <span dangerouslySetInnerHTML={{ __html: ` - ${t('txt.alphanumeric')}` }}/> */}
          </Space>
        </div>

        <div className="form--actions center">
          <Button
            className="btn--ghost mr-10"
            type="primary"
            label={t('action.back')}
            size="large"
            onClick={() => history.push('/login')}
          />

          <Button
            isLoading={status === "loading"}
            type="primary"
            label={
              <React.Fragment>
                <span>{t('action.next')}</span> <ArrowRightOutlined />
              </React.Fragment>
            }
            size="large"
            onClick={handleSubmit}
          />
        </div>
      </Space>
      </form>

      <BasicModal 
        // title={t('action.assignTo')} 
        isClosable={true} 
        isMaskClosable={true} 
        isOpen={showEmailExistPopup} 
        onClose={onCloseModal}>
          <div>
            <p className='my-20' style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 16 }}>
              <InfoCircleOutlined style={{ color: '#40a9ff', fontSize: 20 }}/>
              This email already exist as an individual account, do you still want to proceed or register using other email?
            </p>
            <div className="mt-10" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Button
                type="secondary"
                label={
                  <React.Fragment>
                    <span>{t('action.back')}</span>
                  </React.Fragment>
                }
                size="large"
                onClick={onCloseModal}
              />
              <Button
                type="primary"
                label={
                  <React.Fragment>
                    <span>{t('action.continue')}</span>
                  </React.Fragment>
                }
                size="large"
                onClick={() => onSubmit(validatedValue)}
              />
            </div>
          </div>
      </BasicModal>
    </div>
  )
}

export default AccountInfoForm;
